/**
 * Keyboard shortcuts help modal
 */
import { useEffect, useState, useRef } from 'react';
import { X, Keyboard } from 'lucide-react';
import { Button } from './button';
import { useFocusTrap } from '@/lib/accessibility';

interface ShortcutGroup {
  title: string;
  shortcuts: Array<{
    keys: string[];
    description: string;
  }>;
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['R'], description: 'Refresh current page' },
      { keys: ['ESC'], description: 'Close modal or sidebar' },
      { keys: ['?'], description: 'Show this help dialog' },
    ],
  },
  {
    title: 'Export',
    shortcuts: [
      { keys: ['Ctrl', 'E'], description: 'Export to CSV' },
      { keys: ['Ctrl', 'Shift', 'J'], description: 'Export to JSON' },
    ],
  },
  {
    title: 'Search (Coming Soon)',
    shortcuts: [
      { keys: ['Ctrl', 'K'], description: 'Global search' },
    ],
  },
];

interface KeyboardHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardHelp({ isOpen, onClose }: KeyboardHelpProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Focus trap
  useFocusTrap(isOpen, modalRef);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="keyboard-help-title">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div ref={modalRef} className="relative z-10 w-full max-w-2xl mx-4 bg-white dark:bg-gray-900 border border-border rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20" aria-hidden="true">
              <Keyboard className="h-5 w-5 text-primary" />
            </div>
            <h2 id="keyboard-help-title" className="text-xl font-semibold">Keyboard Shortcuts</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
            aria-label="Close keyboard shortcuts dialog"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            {SHORTCUT_GROUPS.map((group, groupIdx) => (
              <div key={groupIdx}>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {group.title}
                </h3>
                <div className="space-y-2">
                  {group.shortcuts.map((shortcut, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors"
                    >
                      <span className="text-sm text-foreground/90">{shortcut.description}</span>
                      <div className="flex gap-1">
                        {shortcut.keys.map((key, keyIdx) => (
                          <kbd
                            key={keyIdx}
                            className="px-2 py-1 text-xs font-semibold text-foreground bg-muted border border-border rounded shadow-sm"
                          >
                            {key}
                          </kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/30">
          <p className="text-sm text-muted-foreground text-center">
            Press <kbd className="px-2 py-0.5 text-xs font-semibold bg-background border border-border rounded">ESC</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to manage keyboard help modal state
 */
export function useKeyboardHelp() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger in input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // ? key opens help
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}
